/**
 * Self-hosted walkthrough steps — the fallback tier for the onboarding tour.
 *
 * The tour degrades in three stages:
 *   1. Guidejar embed        — full interactive demo (needs REACT_APP_GUIDEJAR_DEMO_URL_*)
 *   2. these self-hosted steps — images served from our own origin, no third party
 *   3. the built-in text slides — always works, zero assets
 *
 * Tier 2 exists so the demo survives Guidejar being unreachable, blocked by a
 * corporate proxy, or simply not configured. Images are served from `public/`,
 * so they add nothing to the JS bundle and are fetched only when the tour opens.
 *
 * To populate: export the recording from Guidejar as PDF (available on the free
 * plan), extract one image per step into `public/demo/<lang>/`, then add an
 * entry below per step. Leave a language empty and it simply falls through to
 * the text slides for that language — a half-filled array is never rendered.
 */

export type DemoStep = {
  /** Path under `public/`, e.g. '/demo/en/01-dashboard.png'. */
  image: string
  /** Short heading, used as the image alt text. */
  title: string
  /** The callout copy — what the viewer should notice on this step. */
  body: string
  /**
   * Where the callout points, as a percentage of the image box. Roughly the
   * element being acted on, the way Guidejar anchors its tooltip to the click
   * target. Defaults to the lower-left, which is safe for most screenshots.
   */
  hotspot?: { x: number; y: number }
}

/**
 * Steps per language. Both are empty until the recording is exported — the
 * tour skips this tier entirely rather than rendering broken image icons.
 */
export const DEMO_STEPS: Record<'en' | 'ka', DemoStep[]> = {
  en: [
    {
      image: '/demo/en/01-dashboard.png',
      title: 'Create your first event',
      body: 'Start here — everything begins with one button.',
      hotspot: { x: 50, y: 64 },
    },
    {
      image: '/demo/en/02-about.png',
      title: 'Tell us about it',
      body: 'Pick the occasion and give it a title. Storyfy tailors the design suggestions to the type of event.',
      hotspot: { x: 50, y: 54 },
    },
    {
      image: '/demo/en/03-when.png',
      title: 'When and where',
      body: 'Add the date, time and place. Choose the address from the suggestions so guests get a real map pin.',
      hotspot: { x: 50, y: 49 },
    },
    {
      image: '/demo/en/04-design.png',
      title: 'Design the invitation',
      body: 'Pick a theme and an accent colour — the preview updates live, exactly as your guests will see it.',
      hotspot: { x: 58, y: 44 },
    },
    {
      image: '/demo/en/05-guests.png',
      title: 'Guest options',
      body: 'Decide whether guests can bring a plus-one or pass the invitation on to others.',
      hotspot: { x: 48, y: 47 },
    },
    {
      image: '/demo/en/06-gifts.png',
      title: 'Gifts and billing',
      body: 'Add a wishlist guests can chip in on, and choose how you pay — free, with 5% of gifts collected.',
      hotspot: { x: 30, y: 50 },
    },
    {
      image: '/demo/en/07-event.png',
      title: 'Share and track',
      body: "That's it. Share the link and watch RSVPs arrive in real time.",
      hotspot: { x: 26, y: 45 },
    },
  ],
  ka: [
    {
      image: '/demo/ka/01-dashboard.png',
      title: 'შექმენი პირველი ღონისძიება',
      body: 'დაიწყე აქედან — ყველაფერი ერთი ღილაკით იწყება.',
      hotspot: { x: 50, y: 64 },
    },
    {
      image: '/demo/ka/02-about.png',
      title: 'მოგვიყევი ღონისძიების შესახებ',
      body: 'აირჩიე ტიპი და დაარქვი სათაური. Storyfy დიზაინის რჩევებს ღონისძიების ტიპს მოარგებს.',
      hotspot: { x: 50, y: 54 },
    },
    {
      image: '/demo/ka/03-when.png',
      title: 'როდის და სად',
      body: 'მიუთითე თარიღი, დრო და ადგილი. აირჩიე მისამართი ჩამონათვალიდან, რომ სტუმრებს რუკაზე პინი დახვდეთ.',
      hotspot: { x: 50, y: 49 },
    },
    {
      image: '/demo/ka/04-design.png',
      title: 'გააფორმე მოწვევა',
      body: 'აირჩიე თემა და აქცენტის ფერი — გადახედვა მყისვე განახლდება, ზუსტად ისე, როგორც სტუმრები ნახავენ.',
      hotspot: { x: 58, y: 44 },
    },
    {
      image: '/demo/ka/05-guests.png',
      title: 'სტუმრების პარამეტრები',
      body: 'გადაწყვიტე, შეძლებენ თუ არა სტუმრები თანმხლების წამოყვანას ან მოწვევის გაზიარებას.',
      hotspot: { x: 48, y: 47 },
    },
    {
      image: '/demo/ka/06-gifts.png',
      title: 'საჩუქრები და გადახდა',
      body: 'დაამატე სურვილების სია და აირჩიე გადახდის გეგმა — უფასო შექმნა, საჩუქრებიდან 5%-ით.',
      hotspot: { x: 30, y: 50 },
    },
    {
      image: '/demo/ka/07-event.png',
      title: 'გააზიარე და თვალი ადევნე',
      body: 'სულ ესაა. გააზიარე ბმული და თვალი ადევნე პასუხებს რეალურ დროში.',
      hotspot: { x: 26, y: 45 },
    },
  ],
}

/**
 * Steps for a language. Deliberately does NOT fall back to the other language:
 * the screenshots show localised UI, so serving English captures to a Georgian
 * viewer reads as broken. Better to drop to the translated text slides.
 */
export const demoStepsFor = (lang: string): DemoStep[] =>
  DEMO_STEPS[lang === 'ka' ? 'ka' : 'en'] ?? []

/** True when we have a self-hosted walkthrough in this specific language. */
export const hasSelfHostedDemo = (lang: string): boolean =>
  demoStepsFor(lang).length > 0
